import { Link } from 'react-router-dom';
import { ArrowRight, AlertTriangle, Loader2, Siren, XCircle } from 'lucide-react';
import { PageHeader, PageBody } from '@/components/layout/AppShell';
import { Card, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { useIncidentList } from '@/api/incidents';
import { useProvisioningQueue, useTenants } from '@/api/tenants';
import { useAuditEntries } from '@/api/audit';
import { relativeTime } from '@/lib/format';

export function OverviewRoute() {
  const { data: incidentList = [] } = useIncidentList();
  const openIncidents = incidentList.filter(i => i.status !== 'resolved');

  const { data: queue = [] } = useProvisioningQueue();
  const activeProvisions = queue.filter(e => e.provision_status !== 'live');

  const { data: apiTenants = [] } = useTenants();
  const tenantCount = apiTenants.length;
  const workspaceCount = apiTenants.reduce((a, t) => a + t.workspace_count, 0);
  const healthyTenants = apiTenants.filter(t => t.worst_workspace_status === 'live').length;

  const { data: audit = [] } = useAuditEntries();
  const recentAudit = audit.slice(0, 6);

  return (
    <>
      <PageHeader
        eyebrow="Platform"
        title="Overview"
        description={
          tenantCount === 0 ? (
            <>Backend not connected — start <code className="font-mono text-xs">uvicorn admin_api.main:app</code> to see live data.</>
          ) : (
            <>
              Health of {tenantCount === 1 ? 'the customer' : `${tenantCount} customers`} and{' '}
              {workspaceCount} instances NousViz Cloud manages today.
              Click a customer to drill in, run actions, view logs.
            </>
          )
        }
      />
      <PageBody>
        {openIncidents.length > 0 && <AlertStrip incidents={openIncidents.length} />}

        {activeProvisions.length > 0 && (
          <Card className="mb-4">
            <CardHeader
              title="Provisioning now"
              description="Instances currently being built or needing attention. Click through for the full step-by-step log."
            />
            <ul className="px-5 py-3 space-y-2.5 text-sm">
              {activeProvisions.map(e => {
                const failed = e.provision_status === 'failed';
                return (
                  <li key={e.workspace_id} className="flex items-center gap-2.5">
                    {failed
                      ? <XCircle size={14} className="text-[color:var(--danger)] shrink-0" />
                      : <Loader2 size={14} className="animate-spin text-accent shrink-0" />}
                    <Link
                      to={`/tenants/${e.tenant_slug}/workspaces/${e.workspace_id}`}
                      className="font-medium text-fg hover:text-accent"
                    >
                      {e.tenant_name ?? e.tenant_slug} / {e.workspace_name}
                    </Link>
                    <span className="text-xs text-fg-subtle truncate">
                      {failed ? (e.last_error ?? 'failed') : e.provision_progress}
                    </span>
                    {e.stalled && !failed && <Badge tone="warn">stalled</Badge>}
                    {failed && <Badge tone="danger">failed</Badge>}
                    {e.elapsed_sec != null && !failed && (
                      <span className="ml-auto text-2xs tabnum text-fg-subtle shrink-0">
                        {Math.floor(e.elapsed_sec / 60)}m {e.elapsed_sec % 60}s
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </Card>
        )}

        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-4">
          <KpiCard label="Customers" value={String(tenantCount)} hint={apiTenants.map(t => t.name).join(' · ') || '—'} />
          <KpiCard label="Instances" value={String(workspaceCount)} hint="Customer NousViz deployments" />
          <KpiCard
            label="Healthy"
            value={tenantCount === 0 ? '—' : `${healthyTenants} / ${tenantCount}`}
            hint={openIncidents.length === 0 ? 'No open incidents' : `${openIncidents.length} open incident${openIncidents.length === 1 ? '' : 's'}`}
            tone={openIncidents.length === 0 ? 'good' : 'bad'}
          />
          <KpiCard
            label="Open incidents"
            value={String(openIncidents.length)}
            hint={openIncidents.length === 0 ? 'All clear' : 'Action required'}
            tone={openIncidents.length === 0 ? 'good' : 'bad'}
          />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="xl:col-span-2 space-y-4">
            <Card>
              <CardHeader
                title="Customers"
                description="The customer organisations this admin manages. Click for detail."
                actions={
                  <Link to="/tenants" className="text-sm text-accent hover:underline inline-flex items-center gap-1">
                    All customers <ArrowRight size={12} />
                  </Link>
                }
              />
              <Table>
                <THead>
                  <tr>
                    <TH>Customer</TH>
                    <TH className="text-right">Instances</TH>
                    <TH>Status</TH>
                    <TH>Support owner</TH>
                  </tr>
                </THead>
                <TBody>
                  {apiTenants.map(t => (
                    <TR key={t.id}>
                      <TD>
                        <Link to={`/tenants/${t.slug}`} className="font-medium text-fg hover:text-accent">
                          {t.name}
                        </Link>
                        <div className="text-2xs text-fg-subtle">{t.slug}</div>
                      </TD>
                      <TD className="text-right tabnum">{t.workspace_count}</TD>
                      <TD>
                        <Badge tone={t.worst_workspace_status === 'live' ? 'success' : 'warn'}>
                          {t.worst_workspace_status}
                        </Badge>
                      </TD>
                      <TD className="text-fg-muted text-xs">{t.operator_owner ?? '—'}</TD>
                    </TR>
                  ))}
                  {apiTenants.length === 0 && (
                    <TR>
                      <TD colSpan={4} className="text-center text-fg-muted py-6">
                        Backend not connected. Start <code className="font-mono text-xs">uvicorn admin_api.main:app</code> to see live data.
                      </TD>
                    </TR>
                  )}
                </TBody>
              </Table>
            </Card>
          </div>

          <div className="space-y-4">
            <Card>
              <CardHeader
                title="Recent activity"
                description="Operator actions and system events."
                actions={<Link to="/audit" className="text-sm text-accent hover:underline">Audit →</Link>}
              />
              <ul className="px-5 py-3 space-y-2.5 text-xs">
                {recentAudit.length === 0 ? (
                  <li className="text-fg-subtle text-center py-2">No events yet.</li>
                ) : recentAudit.map(a => (
                  <li key={a.id} className="flex items-start gap-2">
                    <span className="text-fg-subtle mt-0.5">·</span>
                    <div className="min-w-0">
                      <div className="text-fg">{a.staff_name}</div>
                      <div className="text-fg-muted truncate">
                        <span className="font-mono">{a.action}</span> · {relativeTime(a.timestamp)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>

            <Card>
              <CardHeader title="Open incidents" actions={<Link to="/incidents" className="text-sm text-accent hover:underline">All →</Link>} />
              <ul className="px-5 py-3 space-y-2 text-sm">
                {openIncidents.length === 0 ? (
                  <li className="text-fg-subtle text-center py-2">No open incidents.</li>
                ) : openIncidents.map(i => (
                  <li key={i.id} className="flex items-start gap-2">
                    <Siren size={14} className="text-[color:var(--warn)] mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <Link to={`/incidents/${i.id}`} className="text-fg hover:text-accent">{i.title}</Link>
                      <div className="text-2xs text-fg-subtle">
                        {i.severity.toUpperCase()} · {i.status} · on-call {i.oncall}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </PageBody>
    </>
  );
}

function KpiCard({
  label,
  value,
  hint,
  tone,
}: {
  label: React.ReactNode;
  value: string;
  hint?: string;
  tone?: 'good' | 'bad';
}) {
  return (
    <Card className="px-4 py-3.5">
      <div className="text-2xs uppercase tracking-wider text-fg-subtle font-semibold">{label}</div>
      <div className="text-2xl font-semibold tabnum tracking-tight mt-1">{value}</div>
      {hint && (
        <div className={
          tone === 'good' ? 'text-xs mt-0.5 text-[color:var(--success)]' :
          tone === 'bad' ? 'text-xs mt-0.5 text-[color:var(--danger)]' :
          'text-xs mt-0.5 text-fg-muted'
        }>
          {hint}
        </div>
      )}
    </Card>
  );
}

function AlertStrip({ incidents }: { incidents: number }) {
  return (
    <div className="mb-4 rounded-lg border border-[color:var(--warn)]/40 bg-[color:var(--warn-soft)] px-4 py-2.5 flex items-center gap-4 text-sm">
      <AlertTriangle size={16} className="text-[color:var(--warn)] shrink-0" />
      <Link to="/incidents" className="text-fg hover:text-accent">
        <strong>{incidents}</strong> open incident{incidents === 1 ? '' : 's'}
      </Link>
    </div>
  );
}
